import { produce } from 'immer';

import {
  BearingEnvelope,
  CeilingTray,
  FeaturePatternTrussContainer,
  LayoutFeatureArea,
  PlaneCut,
  TrussEnvelope,
  WorkLine,
} from '@paragon/contract-library';
import { LineSegment2D, Polygon3D } from '@paragon/geometry-library';
import { PlaneEditInstructions, PlaneWithCalculatedGeometry, RoofOrCeilingPlane } from '@paragon/layout';
import { assertExhaustiveSwitch, ReadonlyDictionary, ReadonlyRecord } from '@paragon/utilities';

import { BearingEnvelopeEditInstructions } from '../../store/actions/layout/bearingEnvelopeActions';
import { CeilingTrayEditInstructions } from '../../store/actions/layout/ceilingTrayActions';
import { EditedFeatureArea } from '../../store/actions/layout/featureAreaActions';
import { FeaturePatternContainerEditInstructions } from '../../store/actions/layout/featurePatternContainerActions';
import {
  absolutizeFloorPolylineSegmentGeometry,
  absolutizePlaneGeometry,
  absolutizeRoofPlaneCuts,
  absolutizeTrussEnvelopeCut,
} from './absolutization';
import { ITransformer } from './types';

/*
 * This file contains functions that apply `ITransformer` to various layout objects. It should contain a function for
 *   every `TransformableType`.
 */

// FIXME: flag envelopes that define roof / ceiling planes?
export function transformBearingEnvelopes(
  targets: BearingEnvelope[],
  transformation: ITransformer,
): BearingEnvelopeEditInstructions[] {
  return targets.map(({ guid, leftPoint, rightPoint, top, bottom, justification, levelGuid }) => ({
    guid,
    editedProperties: {
      leftPoint: transformation.applyToPoint(leftPoint),
      rightPoint: transformation.applyToPoint(rightPoint),
      top: transformation.applyToElevation(top),
      bottom: transformation.applyToElevation(bottom),
      justification: transformation.applyToJustification(justification),
      levelGuid: transformation.applyToLevelGuid(levelGuid),
    },
  }));
}

// FIXME: flag envelopes belonging to roof or feature pattern containers?
export function transformTrussEnvelopes(
  targets: TrussEnvelope[],
  transformation: ITransformer,
  dependencies: {
    roofAndCeilingPlanes: ReadonlyDictionary<PlaneWithCalculatedGeometry | null>;
  },
  options: { preserveLinkedComponents: boolean },
): TrussEnvelope[] {
  return targets.map(
    produce((envelope) => {
      envelope.leftPoint = transformation.applyToPoint(envelope.leftPoint);
      envelope.rightPoint = transformation.applyToPoint(envelope.rightPoint);

      envelope.elevation = transformation.applyToElevation(envelope.elevation);

      envelope.justification = transformation.applyToJustification(envelope.justification);

      envelope.levelGuid = transformation.applyToLevelGuid(envelope.levelGuid);
      envelope.featurePatternContainerGuid = transformation.applyToFeaturePatternContainerGuid(envelope);

      if (options.preserveLinkedComponents !== true) {
        envelope.componentDesignGuid = null;
      }

      envelope.topCuts = envelope.topCuts.map((cut) => absolutizeTrussEnvelopeCut(cut, 'Top', dependencies));
      envelope.bottomCuts = envelope.bottomCuts.map((cut) => absolutizeTrussEnvelopeCut(cut, 'Bottom', dependencies));

      if (envelope.leftBevelCut) {
        envelope.leftBevelCut.type = transformation.applyToJustification(envelope.leftBevelCut.type);
      }

      if (envelope.rightBevelCut) {
        envelope.rightBevelCut.type = transformation.applyToJustification(envelope.rightBevelCut.type);
      }
    }),
  );
}

export function transformWorkLines(targets: WorkLine[], transformation: ITransformer): WorkLine[] {
  return targets.map((workLine) => {
    switch (workLine.geometryType) {
      case 'Line':
        return produce(workLine, (draft) => {
          draft.geometry = transformation.applyToLine(draft.geometry);
        });

      case 'Segment':
        return produce(workLine, (draft) => {
          draft.geometry.basePoint = transformation.applyToPoint(draft.geometry.basePoint);
          draft.geometry.endPoint = transformation.applyToPoint(draft.geometry.endPoint);
        });

      default:
        assertExhaustiveSwitch(workLine);
    }
  });
}

// FIXME: flag planes that will be converted to absolute geometry?
export function transformPlanes<T extends RoofOrCeilingPlane>(
  targets: T[],
  transformation: ITransformer,
  dependencies: {
    bearingEnvelopes: ReadonlyDictionary<BearingEnvelope>;
    planes: ReadonlyDictionary<readonly Polygon3D[] | null>;
  },
): PlaneEditInstructions<T>[] {
  return targets.map((plane): PlaneEditInstructions<T> => {
    const absolutizedGeometry = absolutizePlaneGeometry(plane, dependencies);
    const geometry = produce(absolutizedGeometry, (draft) => {
      draft.segment.basePoint = transformation.applyToPoint(draft.segment.basePoint);
      draft.segment.endPoint = transformation.applyToPoint(draft.segment.endPoint);
      draft.elevation = transformation.applyToElevation(draft.elevation);
    });

    const cuts: readonly PlaneCut[] = absolutizeRoofPlaneCuts(dependencies.planes[plane.guid], {
      baseline: LineSegment2D.fromLineSegmentObject(absolutizedGeometry.segment).toLine(),
    }).map((cut) =>
      produce(cut, (draft) => {
        draft.segment.basePoint = transformation.applyToPoint(draft.segment.basePoint);
        draft.segment.endPoint = transformation.applyToPoint(draft.segment.endPoint);
      }),
    );

    return {
      guid: plane.guid,
      editedProperties: { geometry, cuts, levelGuid: transformation.applyToLevelGuid(plane.levelGuid) },
    } as PlaneEditInstructions<T>;
  });
}

export function transformCeilingTrays(
  targets: CeilingTray[],
  transformation: ITransformer,
): CeilingTrayEditInstructions[] {
  return targets.map((tray) => ({
    guid: tray.guid,
    editedProperties: {
      position: transformation.applyToPoint(tray.position),
      topElevation: transformation.applyToElevation(tray.topElevation),
      bottomElevation: transformation.applyToElevation(tray.bottomElevation),
      horizontalAnchor: transformation.applyToHorizontalAnchor(tray.horizontalAnchor),
      rotationAngle: transformation.applyToRotation(tray.rotationAngle),
      levelGuid: transformation.applyToLevelGuid(tray.levelGuid),
    },
  }));
}

export function transformFeatureAreas(
  targets: LayoutFeatureArea[],
  transformation: ITransformer,
  dependencies: {
    featurePatternContainers: ReadonlyRecord<string, FeaturePatternTrussContainer>;

    /**
     * only relevant when the feature areas being transformed have been duplicated: a map of original area guids to
     *   duplicated area guids
     * */
    guidMapping: Map<string, string>;
  },
): EditedFeatureArea[] {
  const containers = Object.values(dependencies.featurePatternContainers);
  function findContainer(desiredAreaGuid: string): FeaturePatternTrussContainer | undefined {
    if (dependencies.guidMapping.has(desiredAreaGuid)) {
      desiredAreaGuid = dependencies.guidMapping.get(desiredAreaGuid)!;
    }

    return containers.find((container) => container.featureAreas.some((area) => area.guid === desiredAreaGuid));
  }

  return targets.map((area) => {
    const container = findContainer(area.guid);

    if (container == null) {
      throw new Error(`Unable to find feature area container for area with guid ${area.guid}`);
    }

    return {
      guid: area.guid,
      containerGuid: container.guid,
      editedArea: transformFeatureArea(area, transformation),
    };
  });
}

function transformFeatureArea(area: LayoutFeatureArea, transformation: ITransformer): LayoutFeatureArea {
  return produce(area, (draft) => {
    draft.area.position = transformation.applyToPoint(draft.area.position);
    draft.area.horizontalAnchor = transformation.applyToHorizontalAnchor(draft.area.horizontalAnchor);
    draft.area.rotationAngle = transformation.applyToRotation(draft.area.rotationAngle);
  });
}

// FIXME: flag feature pattern containers that have segments that will be converted to absolute geometry?
export function transformFeaturePatternContainers(
  targets: FeaturePatternTrussContainer[],
  transformation: ITransformer,
  dependencies: {
    bearingEnvelopes: ReadonlyDictionary<BearingEnvelope>;
  },
): FeaturePatternContainerEditInstructions[] {
  return targets.map((container) => ({
    guid: container.guid,
    changes: {
      levelGuid: transformation.applyToLevelGuid(container.levelGuid),
      shape: produce(container.shape, (draft) => {
        for (let i = 0; i < draft.geometry.length; i++) {
          const section = draft.geometry[i];

          switch (section.type) {
            case 'AgainstBearing': {
              const absoluteGeometry = absolutizeFloorPolylineSegmentGeometry(section, dependencies);

              draft.geometry[i] = {
                ...absoluteGeometry,
                segment: {
                  basePoint: transformation.applyToPoint(absoluteGeometry.segment.basePoint),
                  endPoint: transformation.applyToPoint(absoluteGeometry.segment.endPoint),
                },
              };

              break;
            }

            case 'AbsoluteVertical': {
              section.segment.basePoint = transformation.applyToPoint(section.segment.basePoint);
              section.segment.endPoint = transformation.applyToPoint(section.segment.endPoint);

              break;
            }

            default:
              assertExhaustiveSwitch(section);
          }
        }

        draft.topElevation = transformation.applyToElevation(draft.topElevation);
        draft.bottomElevation = transformation.applyToElevation(draft.bottomElevation);
      }),

      // feature areas are positioned in absolute coordinates, so they must be transformed along with their container
      featureAreas: container.featureAreas.map((area) => transformFeatureArea(area, transformation)),
    },
  }));
}
